import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Gift } from "lucide-react";
import { formatPrice, OtherShoppingVoucherItem } from "../RefProductDetailTypes";
import "./OtherShoppingVouchers.css";

interface OtherShoppingVouchersProps {
  vouchers: OtherShoppingVoucherItem[];
}

const OtherShoppingVouchers = ({ vouchers }: OtherShoppingVouchersProps) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollButtons();
    el.addEventListener("scroll", updateScrollButtons, { passive: true });
    window.addEventListener("resize", updateScrollButtons);
    return () => {
      el.removeEventListener("scroll", updateScrollButtons);
      window.removeEventListener("resize", updateScrollButtons);
    };
  }, [vouchers, updateScrollButtons]);

  const handleScroll = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = 236; // Card width + gap
    const scrollAmount = Math.max(cardWidth * 2, el.clientWidth * 0.75);
    el.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  if (!vouchers || vouchers.length === 0) return null;

  return (
    <section className="other-vouchers-section">
      <div className="other-vouchers-header">
        <div className="other-vouchers-header-text">
          <h3>Other Shopping Vouchers</h3>
          <p>Explore more products with referral shopping voucher benefits</p>
        </div>

        {/* Header navigation arrows */}
        <div className="other-vouchers-header-nav">
          <button
            type="button"
            className="other-vouchers-nav-arrow"
            onClick={() => handleScroll("left")}
            disabled={!canScrollLeft}
            aria-label="Scroll left"
            title="Previous products"
          >
            <ChevronLeft size={18} strokeWidth={2.2} />
          </button>
          <button
            type="button"
            className="other-vouchers-nav-arrow"
            onClick={() => handleScroll("right")}
            disabled={!canScrollRight}
            aria-label="Scroll right"
            title="Next products"
          >
            <ChevronRight size={18} strokeWidth={2.2} />
          </button>
        </div>
      </div>

      <div className="other-vouchers-carousel-wrapper">
        {canScrollLeft && (
          <button
            type="button"
            className="other-vouchers-side-btn prev"
            onClick={() => handleScroll("left")}
            aria-label="Scroll left"
          >
            <ChevronLeft size={20} strokeWidth={2.2} />
          </button>
        )}

        {/* 1 Row Scrollable Track */}
        <div className="other-vouchers-scroll-track" ref={scrollRef}>
          {vouchers.map((item) => (
            <Link
              to={`/Refproductdetails/${item.productId}`}
              key={item.productId}
              className="other-voucher-card"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              <div className="other-voucher-img-wrap">
                <img
                  src={item.productImage || "/assets/images/logos/salarlogo.png"}
                  alt={item.productName}
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.src = "/assets/images/logos/salarlogo.png";
                  }}
                />
                {item.voucherAmount > 0 && (
                  <span className="other-voucher-badge">
                    ₹{formatPrice(item.voucherAmount)} Voucher
                  </span>
                )}
              </div>

              <div className="other-voucher-body">
                <div className="other-voucher-title" title={item.productName}>
                  {item.productName}
                </div>

                <div className="other-voucher-pricing">
                  <div className="other-voucher-price-row">
                    <span className="other-voucher-price">
                      <sup>₹</sup>{formatPrice(item.voucherAmount || item.productPrice)}
                    </span>
                    {item.productPrice > (item.voucherAmount || 0) && item.voucherAmount > 0 && (
                      <span className="other-voucher-mrp">
                        ₹{formatPrice(item.productPrice)}
                      </span>
                    )}
                  </div>

                  {item.voucherValidityDays ? (
                    <div className="other-voucher-extra">
                      <Gift size={12} />
                      <span>{item.voucherValidityDays} days validity</span>
                    </div>
                  ) : null}
                </div>

                <div className="other-voucher-action-btn">
                  View Voucher
                </div>
              </div>
            </Link>
          ))}
        </div>

        {canScrollRight && (
          <button
            type="button"
            className="other-vouchers-side-btn next"
            onClick={() => handleScroll("right")}
            aria-label="Scroll right"
          >
            <ChevronRight size={20} strokeWidth={2.2} />
          </button>
        )}
      </div>
    </section>
  );
};

export default OtherShoppingVouchers;
